import React, { useState } from 'react';
import {
  Activity,
  BarChart3,
  Check,
  ChevronDown,
  ChevronRight,
  DatabaseZap,
  Eye,
  EyeOff,
  Layers,
  RotateCcw,
  SlidersHorizontal,
} from 'lucide-react';
import { CLASSIFICATIONS, DISEASES } from '../mock';

const Section = ({ title, icon, count, open, onToggle, children }) => (
  <div className="border-b border-cyan-900/40 last:border-b-0">
    <button
      onClick={onToggle}
      className="flex w-full items-center gap-2 px-3 py-2.5 transition-colors hover:bg-cyan-500/[0.04]"
    >
      {icon}
      <span className="flex-1 text-left text-[10.5px] font-medium uppercase tracking-[0.16em] text-cyan-300/90">{title}</span>
      {count != null && (
        <span className="text-[11px] text-cyan-200/70">
          <span className="font-semibold text-cyan-50">{count}</span>
        </span>
      )}
      {open ? <ChevronDown className="h-3.5 w-3.5 text-cyan-500" /> : <ChevronRight className="h-3.5 w-3.5 text-cyan-500" />}
    </button>
    {open && <div className="fade-in px-2 pb-2.5 pt-1">{children}</div>}
  </div>
);

const Checkbox = ({ checked, color }) => (
  <span
    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] border transition-colors ${
      checked ? 'border-transparent' : 'border-cyan-800/60 bg-transparent'
    }`}
    style={checked ? { background: color, boxShadow: `0 0 8px ${color}55` } : {}}
  >
    {checked && <Check className="h-2.5 w-2.5 text-[#04141a]" strokeWidth={3.5} />}
  </span>
);

const Select = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="mb-1 block text-[9.5px] uppercase tracking-[0.14em] text-cyan-700">{label}</span>
    <select
      value={value}
      onChange={event => onChange(event.target.value)}
      className="h-8 w-full rounded-md border border-cyan-900/50 bg-[#061417]/85 px-2 text-[11.5px] text-cyan-100 outline-none transition-colors hover:border-cyan-700 focus:border-cyan-500"
    >
      {options.map(option => (
        <option key={option.value || option} value={option.value || option}>
          {option.label || option}
        </option>
      ))}
    </select>
  </label>
);

const BarList = ({ rows, color = '#22d3ee', max = 1, onClick }) => (
  <div className="space-y-1.5">
    {rows.map(([label, count]) => (
      <button
        key={label}
        onClick={() => onClick?.(label)}
        className={`w-full rounded-md px-2 py-1.5 text-left transition-colors ${onClick ? 'hover:bg-cyan-500/[0.06]' : ''}`}
      >
        <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
          <span className="truncate font-medium text-cyan-50">{label}</span>
          <span className="shrink-0 text-cyan-500">{count}</span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-cyan-950/80">
          <div className="h-full rounded-full" style={{ width: `${Math.max(7, (count / max) * 100)}%`, background: color }} />
        </div>
      </button>
    ))}
  </div>
);

const LeftSidebar = ({
  activeDiseases,
  setActiveDiseases,
  classFilter,
  setClassFilter,
  timeWindow,
  setTimeWindow,
  sourceFilter,
  setSourceFilter,
  countryFocus,
  setCountryFocus,
  sourceOptions,
  countryOptions,
  sources,
  snapshotStats,
  usingMock,
  lastUpdatedLabel,
  dashboard,
  onResetFilters,
}) => {
  const [openSnapshot, setOpenSnapshot] = useState(true);
  const [openFilters, setOpenFilters] = useState(true);
  const [openTracking, setOpenTracking] = useState(true);
  const [openLayers, setOpenLayers] = useState(true);
  const [openSources, setOpenSources] = useState(true);

  const toggleDisease = (id) => {
    setActiveDiseases(activeDiseases.includes(id)
      ? activeDiseases.filter(x => x !== id)
      : [...activeDiseases, id]);
  };

  const diseaseMax = Math.max(1, ...dashboard.diseaseCounts.map(([, count]) => count));
  const countryMax = Math.max(1, ...dashboard.countryCounts.map(([, count]) => count));
  const sourceMax = Math.max(1, ...dashboard.sourceCounts.map(([, count]) => count));

  return (
    <aside
      className="outbreak-sidebar fade-in absolute bottom-5 left-3 z-20 max-h-[calc(100vh-90px)] w-[286px] overflow-y-auto rounded-lg border border-cyan-900/40 bg-[#08191d]/95 backdrop-blur-md feed-scroll"
      style={{ boxShadow: '0 10px 34px rgba(0,0,0,0.55)' }}
    >
      <Section
        title="Snapshot"
        icon={<BarChart3 className="h-3.5 w-3.5 text-cyan-400" />}
        open={openSnapshot}
        onToggle={() => setOpenSnapshot(!openSnapshot)}
      >
        <div className="grid grid-cols-3 gap-1.5">
          <div className="rounded-md border border-cyan-900/40 bg-cyan-400/[0.04] px-2 py-2">
            <div className="text-[9px] uppercase tracking-[0.12em] text-cyan-700">Signals</div>
            <div className="mt-0.5 text-[18px] font-semibold leading-none text-cyan-50">{dashboard.signalCount}</div>
          </div>
          <div className="rounded-md border border-cyan-900/40 bg-cyan-400/[0.04] px-2 py-2">
            <div className="text-[9px] uppercase tracking-[0.12em] text-cyan-700">Countries</div>
            <div className="mt-0.5 text-[18px] font-semibold leading-none text-cyan-50">{dashboard.countryCount}</div>
          </div>
          <div className="rounded-md border border-cyan-900/40 bg-cyan-400/[0.04] px-2 py-2">
            <div className="text-[9px] uppercase tracking-[0.12em] text-cyan-700">24h</div>
            <div className="mt-0.5 text-[18px] font-semibold leading-none text-cyan-50">{dashboard.recent24h}</div>
          </div>
        </div>

        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          <div className="rounded-md border border-cyan-900/40 bg-[#061417]/70 px-2 py-2">
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-cyan-700">Top disease</div>
            <div className="mt-0.5 truncate text-[12px] font-medium text-cyan-50">{dashboard.topDisease?.[0] || 'None'}</div>
          </div>
          <div className="rounded-md border border-cyan-900/40 bg-[#061417]/70 px-2 py-2">
            <div className="text-[9.5px] uppercase tracking-[0.14em] text-cyan-700">Top country</div>
            <div className="mt-0.5 truncate text-[12px] font-medium text-cyan-50">{dashboard.topCountry?.[0] || 'None'}</div>
          </div>
        </div>
      </Section>

      <Section
        title="Filters"
        icon={<SlidersHorizontal className="h-3.5 w-3.5 text-cyan-400" />}
        open={openFilters}
        onToggle={() => setOpenFilters(!openFilters)}
      >
        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Window"
            value={timeWindow}
            onChange={setTimeWindow}
            options={[
              { value: '24h', label: 'Last 24h' },
              { value: '7d', label: 'Last 7d' },
              { value: '30d', label: 'Last 30d' },
              { value: 'all', label: 'All time' },
            ]}
          />
          <Select
            label="Source"
            value={sourceFilter}
            onChange={setSourceFilter}
            options={sourceOptions.map(source => ({ value: source, label: source === 'all' ? 'All sources' : source }))}
          />
        </div>
        <div className="mt-2">
          <Select
            label="Country focus"
            value={countryFocus}
            onChange={setCountryFocus}
            options={countryOptions.map(country => ({ value: country, label: country === 'all' ? 'All countries' : country }))}
          />
        </div>
        <button
          onClick={onResetFilters}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-cyan-900/50 px-2 py-1.5 text-[11px] text-cyan-300 transition-colors hover:border-cyan-700 hover:bg-cyan-500/[0.06] hover:text-cyan-100"
        >
          <RotateCcw className="h-3 w-3" /> Reset filters
        </button>
      </Section>

      <Section
        title="Tracking"
        icon={<Activity className="h-3.5 w-3.5 text-cyan-400" />}
        count={`${activeDiseases.length}/${DISEASES.length}`}
        open={openTracking}
        onToggle={() => setOpenTracking(!openTracking)}
      >
        <div className="mb-1.5 flex items-center justify-between px-1.5">
          <button onClick={() => setActiveDiseases(DISEASES.map(d => d.id))} className="text-[10.5px] text-cyan-400 transition-colors hover:text-cyan-200">
            Select all
          </button>
          <span className="text-[10.5px] text-cyan-800">·</span>
          <button onClick={() => setActiveDiseases([])} className="text-[10.5px] text-cyan-500 transition-colors hover:text-cyan-200">
            Clear
          </button>
        </div>
        <div className="space-y-0.5">
          {DISEASES.map(d => {
            const checked = activeDiseases.includes(d.id);
            return (
              <button
                key={d.id}
                onClick={() => toggleDisease(d.id)}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
                  checked ? 'hover:bg-cyan-500/10' : 'opacity-65 hover:bg-cyan-500/5 hover:opacity-100'
                }`}
              >
                <Checkbox checked={checked} color={d.color} />
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: d.color, boxShadow: checked ? `0 0 6px ${d.color}` : 'none' }} />
                <div className="min-w-0 flex-1">
                  <div className={`truncate text-[12px] leading-tight ${checked ? 'font-medium text-cyan-50' : 'text-cyan-300'}`}>{d.name}</div>
                  <div className="truncate text-[9.5px] text-cyan-700">{d.short}</div>
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      <Section
        title="Layers"
        icon={<Layers className="h-3.5 w-3.5 text-cyan-400" />}
        count={dashboard.signalCount}
        open={openLayers}
        onToggle={() => setOpenLayers(!openLayers)}
      >
        <div className="space-y-0.5">
          {Object.values(CLASSIFICATIONS).map(c => {
            const active = classFilter[c.id];
            return (
              <button
                key={c.id}
                onClick={() => setClassFilter({ ...classFilter, [c.id]: !active })}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
                  active ? 'hover:bg-cyan-500/10' : 'opacity-55 hover:bg-cyan-500/5 hover:opacity-90'
                }`}
              >
                <Checkbox checked={active} color={c.color} />
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: c.color, boxShadow: active ? `0 0 6px ${c.color}` : 'none' }} />
                <span className="flex-1 text-[12px] font-medium text-cyan-50">{c.label}</span>
                <span className="text-[10.5px] text-cyan-600">{dashboard.classCounts[c.id] || 0}</span>
                {active ? <Eye className="h-3 w-3 text-cyan-500/80" /> : <EyeOff className="h-3 w-3 text-cyan-700" />}
              </button>
            );
          })}
        </div>
        <p className="mt-2 px-1 text-[10px] leading-relaxed text-cyan-700/90">
          Pins are news signals, not confirmed case counts.
        </p>
      </Section>

      <Section
        title="Sources"
        icon={<DatabaseZap className="h-3.5 w-3.5 text-cyan-400" />}
        count={sources.length || dashboard.sourceCounts.length}
        open={openSources}
        onToggle={() => setOpenSources(!openSources)}
      >
        {sources.length > 0 ? (
          <div className="space-y-1.5">
            {sources.map(source => (
              <button
                key={source.id || source.label}
                onClick={() => setSourceFilter(source.label)}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-cyan-900/35 bg-[#061417]/60 px-2 py-1.5 text-left transition-colors hover:border-cyan-700/60 hover:bg-cyan-500/[0.05]"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[11.5px] font-medium text-cyan-50">{source.label}</span>
                  <span className={source.ok ? 'text-[10px] text-emerald-300/80' : 'text-[10px] text-amber-300/90'}>
                    {source.ok ? 'healthy' : source.error || 'error'}
                  </span>
                </span>
                <span className="shrink-0 text-[11px] text-cyan-400">{source.count}</span>
              </button>
            ))}
          </div>
        ) : (
          <BarList rows={dashboard.sourceCounts} max={sourceMax} onClick={setSourceFilter} />
        )}
        <div className="mt-2 text-[10px] leading-relaxed text-cyan-700">
          Snapshot: {usingMock ? 'demo fallback' : 'public R2'} · {lastUpdatedLabel}
          {snapshotStats?.totalSignals ? ` · ${snapshotStats.totalSignals} total` : ''}
        </div>
      </Section>

      {dashboard.diseaseCounts.length > 0 && (
        <div className="border-t border-cyan-900/40 p-2.5">
          <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.16em] text-cyan-300/90">Top Diseases</div>
          <BarList rows={dashboard.diseaseCounts.slice(0, 5)} max={diseaseMax} color="#67e8f9" />
        </div>
      )}

      {dashboard.countryCounts.length > 0 && (
        <div className="border-t border-cyan-900/40 p-2.5">
          <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.16em] text-cyan-300/90">Top Countries</div>
          <BarList rows={dashboard.countryCounts.slice(0, 5)} max={countryMax} color="#34d399" onClick={setCountryFocus} />
        </div>
      )}

      {dashboard.sourceCounts.length > 0 && (
        <div className="border-t border-cyan-900/40 p-2.5">
          <div className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.16em] text-cyan-300/90">Source Mix</div>
          <BarList rows={dashboard.sourceCounts.slice(0, 5)} max={sourceMax} color="#fbbf24" onClick={setSourceFilter} />
        </div>
      )}
    </aside>
  );
};

export default LeftSidebar;
